/**
 * tourSteps/milestoneSteps.js
 * Comprehensive tour steps for Milestones & Delivery Governance.
 * Thoroughly explains the Milestone Card anatomy, phase progress, deliverables verified,
 * and interactively opens the Milestone Drawer to guide through progress cards,
 * completion criteria, checklists, child tasks, and CRE client synchronization.
 */

export const milestoneSteps = [
    {
        title: 'Project Milestones Command',
        description: 'Milestones represent high-level project phases, contractual client deliverables, or critical approval gates. Track major phases across all active initiatives.',
        selector: '[data-tour="milestone-header"]',
        placement: 'bottomLeft',
    },
    {
        title: 'Milestone Toolbar & Lifecycle Filter',
        description: 'Filter milestone phases by status: All, Backlog, In Progress, Under Review, Completed, On Hold, or Cancelled. Use the keyword search bar to find milestones instantly.',
        selector: '[data-tour="milestone-toolbar"]',
        fallbackSelector: '[data-tour="milestone-header"]',
        placement: 'bottom',
    },
    {
        title: 'Create & Plan Milestones',
        description: 'Define target completion deadlines, assign responsible milestone owners, and establish required phase deliverables for executive sign-off.',
        selector: '[data-tour="create-milestone-btn"]',
        fallbackSelector: '[data-tour="milestone-toolbar"]',
        placement: 'bottomRight',
    },
    {
        title: 'Milestone Card: Title & Identity',
        description: 'Each card represents a critical project gate. The gold diamond ◆ indicates a Milestone item type, alongside the parent project badge and live execution status.',
        selector: '[data-tour="first-milestone-card"]',
        fallbackSelector: '[data-tour="milestone-card-list"]',
        placement: 'bottom',
    },
    {
        title: 'Milestone Phase Progress Gauge',
        description: 'Progress is automatically calculated from all connected child tasks. As team members complete tasks in the backlog or board, the milestone progress bar dynamically advances towards 100%.',
        selector: '[data-tour="milestone-card-progress"]',
        fallbackSelector: '[data-tour="first-milestone-card"]',
        placement: 'bottom',
    },
    {
        title: 'Deliverables Verification Counter',
        description: 'Tracks contractual deliverables (e.g. "3/4 Met"). Displays the exact count of verified outputs required before milestone sign-off.',
        selector: '[data-tour="milestone-card-deliverables"]',
        fallbackSelector: '[data-tour="first-milestone-card"]',
        placement: 'bottom',
    },
    {
        title: 'Milestone Owner & Target Deadline',
        description: 'Displays the accountable Milestone Owner accountable for deliverable verification, plus the target calendar deadline and total connected child tasks.',
        selector: '[data-tour="milestone-card-owner"]',
        fallbackSelector: '[data-tour="first-milestone-card"]',
        placement: 'bottom',
    },
    {
        title: 'Milestone Progress Card (Drawer)',
        description: 'Inspect detailed progress metrics: total connected child tasks, completed count, and remaining work items contributing to this milestone phase.',
        selector: '[data-tour="milestone-progress-card"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'bottom',
        isDrawerStep: true,
    },
    {
        title: 'PM Completion Criteria & Templates',
        description: 'Formal sign-off criteria specified by project managers. Click suggestion chips to quickly insert standard acceptance criteria (e.g. client sign-off, QA test pass, zero critical bugs).',
        selector: '[data-tour="milestone-completion-criteria"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'bottom',
        isDrawerStep: true,
    },
    {
        title: 'Mandatory Deliverables Checklist',
        description: 'A contractual deliverables verification list. Check off verified client assets, mockups, or reports before submitting the milestone for approval.',
        selector: '[data-tour="milestone-deliverables-checklist"]',
        fallbackSelector: '[data-tour="task-subtasks-section"]',
        placement: 'top',
        isDrawerStep: true,
    },
    {
        title: 'Connected Child Tasks',
        description: 'Actionable work items executed by individual team members that roll up into this milestone. Add or reassign child tasks directly within the drawer.',
        selector: '[data-tour="task-subtasks-section"]',
        placement: 'top',
        isDrawerStep: true,
    },
    {
        title: 'CRE Client Portal Sync & Completion',
        description: 'When all deliverables are verified and status is changed to "Completed", the system automatically validates checklist items and syncs approval status directly with the client CRE portal.',
        selector: '[data-tour="task-header-status"]',
        placement: 'bottomLeft',
        isDrawerStep: true,
    },
];
